import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'baseline-collection' | 'baseline-output' | 'transition-to-diagnosis' | 'diagnostic-questions' | 'full-report';
export function DeepSelfDecode() {
  const navigate = useNavigate();
  // TODO: Check if user is authenticated and has baseline
  const [isAuthenticated] = useState(true);
  const [hasBaseline] = useState(false);
  // Start at baseline-collection if no baseline exists
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasBaseline ? 'baseline-output' : 'baseline-collection');
  const [showBaselineExplanation, setShowBaselineExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  const [questionProgress, setQuestionProgress] = useState(0);
  // Baseline data (reusing QuickSelfDecode structure)
  const [baselineData, setBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Diagnostic questions data
  const [diagnosticData, setDiagnosticData] = useState({
    patternRepetition: [] as string[],
    formationConditions: [] as string[],
    constraintMechanisms: [] as string[],
    reinforcementLoops: [] as string[],
    awarenessTiming: '',
    triggerIntensity: '',
    responsePattern: '',
    contextualInput: ''
  });
  // Baseline output (structural foundation)
  const baselineOutput = {
    structuralOrientation: 'You are wired to observe and stabilize systems before fully entering them. When complexity rises, you move toward clarity — understanding patterns, naming dynamics, and organizing what feels scattered. This orientation is structural, not learned. It shapes how you process information, make decisions, and stabilize under pressure.',
    primaryTension: 'At the core of this system is a recurring pull between control and surrender — between architecting reality with precision and letting experience move you without managing it. This tension is not a problem to solve. It is the engine that drives how you engage with complexity.',
    structuralBlindSpot: 'Clarity can sometimes feel like completion. Understanding a pattern may arrive faster than emotional or bodily integration. The system can mistake cognitive resolution for systemic change, leading to repeated loops where insight does not translate to different action.'
  };
  // Full diagnostic output
  const fullDiagnosticOutput = {
    patternMechanics: 'When uncertainty or complexity rises, the system automatically moves toward analysis and structure-building. The trigger is ambiguity; the response is to map, name, and organize. This creates temporary stability but can delay embodied action. The pattern reinforces itself: clarity feels like progress, which rewards the pattern, which makes it more automatic.',
    patternOrigin: 'This pattern likely formed in conditions where clarity was scarce and consequences were high. Early environments may have rewarded precision, punished ambiguity, or required you to stabilize chaos for others. The system learned that understanding = safety, and that pattern became structural.',
    constraintMap: 'The pattern stays active because: (1) it reliably reduces anxiety in the short term, (2) external systems reward analysis over embodied action, (3) the cost of the pattern (delayed action, emotional bypass) is less visible than the benefit (immediate clarity). The constraint is not external pressure — it is that the pattern works well enough to stay invisible.',
    crossContextView: {
      work: 'At work, this shows up as over-preparation, analysis paralysis, or needing full clarity before committing. You may delay decisions until you have "enough information," which often means more than necessary.',
      relationships: 'In relationships, this can appear as emotional distance, intellectualizing conflict, or needing to "figure out" feelings before expressing them. Intimacy may feel destabilizing because it resists clear mapping.',
      decisions: 'Major decisions trigger the pattern most intensely. You may loop in analysis, seek external validation disguised as "more data," or feel frozen between options because neither feels perfectly clear.'
    },
    whatChangedFromBaseline: {
      baseline: 'The baseline showed your structural orientation (observer-stabilizer), core tension (control vs surrender), and blind spot (clarity as completion).',
      diagnosis: 'The diagnostic questions revealed: how the pattern formed (early conditions requiring clarity for safety), how it reinforces itself (clarity = reward = more automatic), what keeps it active (short-term anxiety reduction), and where it repeats across contexts (work, relationships, decisions).',
      whyBaselineAlone: 'Baseline shows what kind of system this is. Diagnosis shows how the system formed, why it stays active, and where it creates friction or leverage. Baseline is structure; diagnosis is mechanism and repetition.'
    },
    whatChangedFromSnapshot: {
      quickSelfSnapshot: 'The Quick Self Decode showed your baseline pattern: over-preparation, analysis paralysis, and emotional distance. It identified the core tension (control vs surrender) and the blind spot (clarity as completion).',
      deepDiagnosis: 'The deep diagnosis revealed the pattern mechanics, origin, and constraint map. It showed how the pattern forms in early life, how it reinforces itself, and what keeps it active. It also identified the cross-context repetition and the boundary conditions.',
      whySnapshotInsufficient: 'The Quick Self Decode alone could not show the full picture. It identified the pattern but not the mechanics, origin, or constraint map. It could not explain why the pattern repeats across contexts or why it stays active despite awareness.'
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleBaselineSubmit = () => {
    if (!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country) return;
    if ((baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating your structural baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('baseline-output');
    }, 2000);
  };
  const handleStartDiagnosis = () => {
    setCurrentPhase('transition-to-diagnosis');
  };
  const handleBeginQuestions = () => {
    setCurrentPhase('diagnostic-questions');
    setQuestionProgress(0);
  };
  const handleDiagnosticSubmit = () => {
    if (!diagnosticData.awarenessTiming || !diagnosticData.triggerIntensity || !diagnosticData.responsePattern) return;
    if (diagnosticData.patternRepetition.length === 0 || diagnosticData.formationConditions.length === 0) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating full diagnostic...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('full-report');
    }, 3000);
  };
  const handleToggleMultiSelect = (field: keyof typeof diagnosticData, value: string) => {
    setDiagnosticData(prev => {
      const currentArray = prev[field] as string[];
      return {
        ...prev,
        [field]: currentArray.includes(value) ? currentArray.filter(v => v !== value) : [...currentArray, value]
      };
    });
  };
  if (isGenerating) {
    return <>
        <Helmet data-id="element-1175">
          <title data-id="element-1176">Generating Diagnostic | Lazy Monk Labs</title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-1177">
          <div className="text-center" data-id="element-1178">
            <div className="mb-6" data-id="element-1179">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-1180"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-1181">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-1182">
        <title data-id="element-1183">Deep Self Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-1184">
        <header data-id="element-1185">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-1186">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1187">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-1188">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-1189" />
                <span className="text-lg font-medium tracking-tight" data-id="element-1190">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-1191">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-1192">
          <div className="max-w-2xl mx-auto" data-id="element-1193">
            {/* PHASE 1: Baseline Collection */}
            {currentPhase === 'baseline-collection' && <div data-id="element-1194">
                <div className="mb-12" data-id="element-1195">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1196">
                    Deep Self Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-1197">
                    Structural baseline
                  </p>
                </div>

                {/* Reuse baseline collection UI from QuickSelfDecode */}
                <div className="space-y-8 mb-12" data-id="element-1198">
                  <div data-id="element-1199">
                    <label className="block text-sm font-medium mb-3" data-id="element-1200">
                      Date of birth
                    </label>
                    {baselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-1201">
                        <div className="text-2xl font-medium" data-id="element-1202">
                          {months.find(m => m.value === baselineData.month)?.label}{' '}
                          {baselineData.day}, {baselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-1203">
                      <div data-id="element-1204">
                        <label htmlFor="month" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-1205">
                          Month
                        </label>
                        <select id="month" value={baselineData.month} onChange={e => updateDateOfBirth(baselineData.year, e.target.value, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1206">
                          <option value="" data-id="element-1207">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-1208">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-1209">
                        <label htmlFor="day" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-1210">
                          Day
                        </label>
                        <select id="day" value={baselineData.day} onChange={e => updateDateOfBirth(baselineData.year, baselineData.month, e.target.value)} disabled={!baselineData.month || !baselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-1211">
                          <option value="" data-id="element-1212">Day</option>
                          {Array.from({
                        length: getDaysInMonth(baselineData.year, baselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-1213">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-1214">
                        <label htmlFor="year" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-1215">
                          Year
                        </label>
                        <select id="year" value={baselineData.year} onChange={e => updateDateOfBirth(e.target.value, baselineData.month, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1216">
                          <option value="" data-id="element-1217">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-1218">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Time of Birth - Same as QuickSelfDecode */}
                  <div data-id="element-1219">
                    <label className="block text-sm font-medium mb-3" data-id="element-1220">
                      Time of birth{' '}
                      <span className="text-[#F5F5F5]/40" data-id="element-1221">(optional)</span>
                    </label>
                    <div className="space-y-3 mb-4" data-id="element-1222">
                      {[{
                    value: 'exact',
                    label: 'I know the exact time'
                  }, {
                    value: 'approximate',
                    label: 'I know approximately'
                  }, {
                    value: 'not-sure',
                    label: "Not sure / Don't know"
                  }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${baselineData.timeKnown === option.value ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1223">
                          <input type="radio" name="timeKnown" value={option.value} checked={baselineData.timeKnown === option.value} onChange={e => setBaselineData(prev => ({
                      ...prev,
                      timeKnown: e.target.value,
                      timeOfBirth: '',
                      hour: '12',
                      minute: '00',
                      period: 'AM'
                    }))} className="sr-only" data-id="element-1224" />
                          <span className="text-sm" data-id="element-1225">{option.label}</span>
                        </label>)}
                    </div>
                    {(baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && <div className="mt-6" data-id="element-1226">
                        <div className="bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-1227">
                          <div className="text-center mb-8" data-id="element-1228">
                            <div className="text-4xl font-medium tracking-tight" data-id="element-1229">
                              {baselineData.hour}:{baselineData.minute}{' '}
                              {baselineData.period}
                            </div>
                            <p className="text-xs text-[#F5F5F5]/40 mt-2" data-id="element-1230">
                              {baselineData.timeKnown === 'approximate' ? 'Your best estimate is fine' : 'Select the exact time'}
                            </p>
                          </div>
                          <div className="mb-6" data-id="element-1231">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-1232">
                              Hour
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-1233">
                              {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updateTimeOfBirth(h, baselineData.minute, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.hour === h ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1234">
                                  {h}
                                </button>)}
                            </div>
                          </div>
                          <div className="mb-6" data-id="element-1235">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-1236">
                              Minute
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-1237">
                              {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, m, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.minute === m ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1238">
                                  {m}
                                </button>)}
                            </div>
                            <div className="mt-2" data-id="element-1239">
                              <input type="number" min="0" max="59" value={baselineData.minute} onChange={e => {
                          const val = e.target.value.padStart(2, '0');
                          if (parseInt(val) >= 0 && parseInt(val) <= 59) updateTimeOfBirth(baselineData.hour, val, baselineData.period);
                        }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1240" />
                            </div>
                          </div>
                          <div data-id="element-1241">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-1242">
                              Period
                            </label>
                            <div className="grid grid-cols-2 gap-2" data-id="element-1243">
                              {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, baselineData.minute, p)} className={`py-3 text-sm font-medium transition-colors ${baselineData.period === p ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1244">
                                  {p}
                                </button>)}
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>

                  <div data-id="element-1245">
                    <label htmlFor="placeOfBirth" className="block text-sm font-medium mb-2" data-id="element-1246">
                      Place of birth
                    </label>
                    <input type="text" id="placeOfBirth" value={baselineData.placeOfBirth} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1247" />
                  </div>

                  <div data-id="element-1248">
                    <label htmlFor="country" className="block text-sm font-medium mb-2" data-id="element-1249">
                      Country
                    </label>
                    <input type="text" id="country" value={baselineData.country} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-1250" />
                  </div>
                </div>

                <button onClick={handleBaselineSubmit} disabled={!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country || (baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1251">
                  Generate Structural Baseline
                </button>
              </div>}

            {/* PHASE 2: Baseline Output (Deep Version) */}
            {currentPhase === 'baseline-output' && <div data-id="element-1252">
                <div className="mb-12" data-id="element-1253">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1254">
                    Deep Self Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-1255">
                    Structural baseline
                  </p>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1256">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1257">
                    <span className="text-2xl" data-id="element-1258">🧠</span>
                    <div className="flex-1" data-id="element-1259">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1260">
                        Structural Orientation
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1261">
                        {baselineOutput.structuralOrientation}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1262">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1263">
                    <span className="text-2xl" data-id="element-1264">⚖️</span>
                    <div className="flex-1" data-id="element-1265">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1266">
                        Primary Tension Axis
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1267">
                        {baselineOutput.primaryTension}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1268">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1269">
                    <span className="text-2xl" data-id="element-1270">⚠️</span>
                    <div className="flex-1" data-id="element-1271">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1272">
                        Structural Blind Spot
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1273">
                        {baselineOutput.structuralBlindSpot}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 text-center" data-id="element-1274">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-1275">
                    Structural Baseline — remains constant across time
                  </p>
                </div>

                <div className="mb-12" data-id="element-1276">
                  <button onClick={() => setShowBaselineExplanation(!showBaselineExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-1277">
                    <span data-id="element-1278">📘</span>
                    <span data-id="element-1279">How this report is generated</span>
                    <span className="text-xs" data-id="element-1280">
                      {showBaselineExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showBaselineExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1281">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1282">
                        This report starts with your structural baseline — the
                        parts of you that do not change with mood or
                        circumstance. Birth data is used as coordinates to map
                        how this system processes information and stabilizes
                        tension. The next questions do not change structure.
                        They diagnose how this structure formed, repeats, and
                        stays active.
                      </p>
                    </div>}
                </div>

                <button onClick={handleStartDiagnosis} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1283">
                  Continue to Diagnosis
                </button>
              </div>}

            {/* PHASE 3: Transition to Diagnosis */}
            {currentPhase === 'transition-to-diagnosis' && <div data-id="element-1284">
                <div className="mb-12" data-id="element-1285">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1286">
                    From structure to diagnosis
                  </h1>
                </div>

                <div className="mb-12" data-id="element-1287">
                  <p className="text-lg text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-1288">
                    The baseline shows what kind of system this is.
                  </p>
                  <p className="text-lg text-[#F5F5F5]/70 leading-relaxed" data-id="element-1289">
                    The next questions diagnose how this system formed, repeats
                    across life, and stays active.
                  </p>
                </div>

                <button onClick={handleBeginQuestions} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1290">
                  Begin Diagnostic Questions
                </button>
              </div>}

            {/* PHASE 4: Diagnostic Questions */}
            {currentPhase === 'diagnostic-questions' && <div data-id="element-1291">
                <div className="mb-12" data-id="element-1292">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1293">
                    Diagnostic Questions
                  </h1>
                  <div className="flex items-center gap-2 text-xs text-[#F5F5F5]/40" data-id="element-1294">
                    <div className="flex-1 bg-[#2B2B2B] h-1 rounded-full overflow-hidden" data-id="element-1295">
                      <div className="bg-[#F5F5F5] h-full transition-all" style={{
                    width: `${questionProgress / 8 * 100}%`
                  }} data-id="element-1296"></div>
                    </div>
                    <span data-id="element-1297">{questionProgress} of 8</span>
                  </div>
                </div>

                <div className="space-y-12 mb-12" data-id="element-1298">
                  {/* Question 1: Pattern Repetition */}
                  <div data-id="element-1299">
                    <h2 className="text-lg font-medium mb-3" data-id="element-1300">
                      Where does this pattern show up most in your life?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-1301">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-1302">
                      {['Work and professional decisions', 'Close relationships', 'Major life transitions', 'Daily routines and habits', 'Conflict or high-pressure situations'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.patternRepetition.includes(option) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1303">
                          <input type="checkbox" checked={diagnosticData.patternRepetition.includes(option)} onChange={() => handleToggleMultiSelect('patternRepetition', option)} className="sr-only" data-id="element-1304" />
                          <span className="text-base" data-id="element-1305">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 2: Formation Conditions */}
                  <div data-id="element-1306">
                    <h2 className="text-lg font-medium mb-3" data-id="element-1307">
                      What conditions in your early life may have shaped this
                      pattern?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-1308">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-1309">
                      {['Environments that rewarded clarity and precision', 'Situations where ambiguity felt unsafe', 'Responsibility for stabilizing chaos or others', 'High consequences for mistakes or uncertainty', 'Limited emotional support or validation'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.formationConditions.includes(option) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1310">
                          <input type="checkbox" checked={diagnosticData.formationConditions.includes(option)} onChange={() => handleToggleMultiSelect('formationConditions', option)} className="sr-only" data-id="element-1311" />
                          <span className="text-base" data-id="element-1312">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 3: Awareness Timing */}
                  <div data-id="element-1313">
                    <h2 className="text-lg font-medium mb-6" data-id="element-1314">
                      When do you typically become aware of this pattern?
                    </h2>
                    <div className="space-y-3" data-id="element-1315">
                      {['Before it activates (I can see it coming)', "While it's happening (I notice in real-time)", "After it's over (only in reflection)", 'Rarely aware of it at all'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.awarenessTiming === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1316">
                          <input type="radio" name="awarenessTiming" value={option} checked={diagnosticData.awarenessTiming === option} onChange={e => setDiagnosticData(prev => ({
                      ...prev,
                      awarenessTiming: e.target.value
                    }))} className="sr-only" data-id="element-1317" />
                          <span className="text-base" data-id="element-1318">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 4: Trigger Intensity */}
                  <div data-id="element-1319">
                    <h2 className="text-lg font-medium mb-6" data-id="element-1320">
                      How intense does the trigger need to be for this pattern
                      to activate?
                    </h2>
                    <div className="space-y-3" data-id="element-1321">
                      {['Very low (activates easily)', 'Moderate (needs some pressure)', 'High (only under significant stress)', 'Extreme (only in crisis)'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.triggerIntensity === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1322">
                          <input type="radio" name="triggerIntensity" value={option} checked={diagnosticData.triggerIntensity === option} onChange={e => setDiagnosticData(prev => ({
                      ...prev,
                      triggerIntensity: e.target.value
                    }))} className="sr-only" data-id="element-1323" />
                          <span className="text-base" data-id="element-1324">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 5: Response Pattern */}
                  <div data-id="element-1325">
                    <h2 className="text-lg font-medium mb-6" data-id="element-1326">
                      What do you typically do when this pattern activates?
                    </h2>
                    <div className="space-y-3" data-id="element-1327">
                      {['Analyze and plan extensively', 'Withdraw and process internally', 'Seek external validation or input', 'Push through with action', 'Freeze or avoid the situation'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.responsePattern === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1328">
                          <input type="radio" name="responsePattern" value={option} checked={diagnosticData.responsePattern === option} onChange={e => setDiagnosticData(prev => ({
                      ...prev,
                      responsePattern: e.target.value
                    }))} className="sr-only" data-id="element-1329" />
                          <span className="text-base" data-id="element-1330">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 6: Contextual Input (Free Text) */}
                  <div data-id="element-1331">
                    <h2 className="text-lg font-medium mb-3" data-id="element-1332">
                      Anything important this diagnostic should factor in?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-1333">
                      A situation, relationship, or theme you're currently
                      navigating.
                    </p>
                    <textarea value={diagnosticData.contextualInput} onChange={e => setDiagnosticData(prev => ({
                  ...prev,
                  contextualInput: e.target.value
                }))} rows={4} maxLength={300} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional context..." data-id="element-1334" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-1335">
                      {diagnosticData.contextualInput.length}/300
                    </p>
                  </div>
                </div>

                <button onClick={handleDiagnosticSubmit} disabled={!diagnosticData.awarenessTiming || !diagnosticData.triggerIntensity || !diagnosticData.responsePattern || diagnosticData.patternRepetition.length === 0 || diagnosticData.formationConditions.length === 0} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1336">
                  Generate Full Diagnostic
                </button>
              </div>}

            {/* PHASE 5: Full Report */}
            {currentPhase === 'full-report' && <div data-id="element-1337">
                <div className="mb-12" data-id="element-1338">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1339">
                    Deep Self Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-1340">Full Diagnostic</p>
                </div>

                {/* Section 1: Structural Baseline (Condensed) */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1341">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1342">
                    <span className="text-2xl" data-id="element-1343">🧱</span>
                    <div className="flex-1" data-id="element-1344">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-1345">
                        Structural Baseline (Unchanged)
                      </p>
                      <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1346">
                        Observer-stabilizer orientation. Core tension: control
                        vs surrender. Blind spot: clarity as completion.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 2: Pattern Mechanics */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1347">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1348">
                    <span className="text-2xl" data-id="element-1349">🔁</span>
                    <div className="flex-1" data-id="element-1350">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1351">
                        Pattern Mechanics
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1352">
                        {fullDiagnosticOutput.patternMechanics}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 3: Pattern Origin */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1353">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1354">
                    <span className="text-2xl" data-id="element-1355">🧬</span>
                    <div className="flex-1" data-id="element-1356">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1357">
                        Pattern Origin
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1358">
                        {fullDiagnosticOutput.patternOrigin}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 4: Constraint Map */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1359">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1360">
                    <span className="text-2xl" data-id="element-1361">🔒</span>
                    <div className="flex-1" data-id="element-1362">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1363">
                        Constraint Map
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1364">
                        {fullDiagnosticOutput.constraintMap}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 5: Cross-Context View */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1365">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1366">
                    <span className="text-2xl" data-id="element-1367">🌐</span>
                    <div className="flex-1" data-id="element-1368">
                      <h2 className="text-xl font-medium mb-6" data-id="element-1369">
                        Cross-Context View
                      </h2>
                      <div className="space-y-6" data-id="element-1370">
                        <div data-id="element-1371">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1372">
                            Work
                          </h3>
                          <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1373">
                            {fullDiagnosticOutput.crossContextView.work}
                          </p>
                        </div>
                        <div data-id="element-1374">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1375">
                            Relationships
                          </h3>
                          <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1376">
                            {fullDiagnosticOutput.crossContextView.relationships}
                          </p>
                        </div>
                        <div data-id="element-1377">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1378">
                            Decisions
                          </h3>
                          <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1379">
                            {fullDiagnosticOutput.crossContextView.decisions}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 6: What Changed from Baseline */}
                <div className="mb-12" data-id="element-1380">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-1381">
                    <span data-id="element-1382">🔍</span>
                    <span data-id="element-1383">What changed after deep diagnosis</span>
                    <span className="text-xs" data-id="element-1384">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-1385">
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1386">
                        <h3 className="text-sm font-medium mb-4" data-id="element-1387">
                          What Quick Self Decode showed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1388">
                          {fullDiagnosticOutput.whatChangedFromSnapshot.quickSelfSnapshot}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1389">
                        <h3 className="text-sm font-medium mb-4" data-id="element-1390">
                          What deep diagnosis revealed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1391">
                          {fullDiagnosticOutput.whatChangedFromSnapshot.deepDiagnosis}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1392">
                        <h3 className="text-sm font-medium mb-4" data-id="element-1393">
                          Why snapshot alone was insufficient
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1394">
                          {fullDiagnosticOutput.whatChangedFromSnapshot.whySnapshotInsufficient}
                        </p>
                      </div>
                    </div>}
                </div>

                {/* CLOSURE PHASE 1: Pattern Consistency Check */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1395">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1396">
                    <span className="text-2xl" data-id="element-1397">🔁</span>
                    <div className="flex-1" data-id="element-1398">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1399">
                        Pattern Consistency Check
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-1400">
                        This same pattern tends to appear when external pressure
                        rises, when clarity is absent, or when control feels
                        threatened. Across different life contexts, the same
                        mechanism repeats.
                      </p>
                      <div className="space-y-4" data-id="element-1401">
                        <div data-id="element-1402">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1403">
                            At work
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1404">
                            The pattern activates under ambiguous authority,
                            unclear expectations, or when responsibility exceeds
                            control. You over-function to create internal
                            structure, which creates short-term relief but
                            long-term exhaustion.
                          </p>
                        </div>
                        <div data-id="element-1405">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1406">
                            In relationships
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1407">
                            The pattern appears when emotional intensity rises
                            or when the other person's behavior feels
                            unpredictable. You withdraw into analysis, which
                            creates distance, which reinforces the pattern.
                          </p>
                        </div>
                        <div data-id="element-1408">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1409">
                            In decision-making
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1410">
                            The pattern delays action when stakes feel high or
                            when multiple variables exist. You seek more
                            information to reduce uncertainty, which delays the
                            decision, which increases pressure.
                          </p>
                        </div>
                        <div data-id="element-1411">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1412">
                            Under stress
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1413">
                            The pattern intensifies when external chaos
                            increases. You tighten control internally, which
                            creates temporary stability but reduces adaptability
                            and increases rigidity over time.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CLOSURE PHASE 2: Boundary Conditions */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1414">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1415">
                    <span className="text-2xl" data-id="element-1416">⚖️</span>
                    <div className="flex-1" data-id="element-1417">
                      <h2 className="text-xl font-medium mb-6" data-id="element-1418">
                        Where This Diagnosis Applies — and Where It Doesn't
                      </h2>
                      <div className="space-y-6" data-id="element-1419">
                        <div data-id="element-1420">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-1421">
                            This diagnosis explains:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-1422">
                            <li data-id="element-1423">
                              • Core pattern mechanics and how they formed
                            </li>
                            <li data-id="element-1424">
                              • Repeating internal conflicts across contexts
                            </li>
                            <li data-id="element-1425">
                              • Why certain situations feel disproportionately
                              intense
                            </li>
                            <li data-id="element-1426">
                              • What keeps the pattern active despite awareness
                            </li>
                          </ul>
                        </div>
                        <div data-id="element-1427">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-1428">
                            This diagnosis does NOT explain:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-1429">
                            <li data-id="element-1430">• Every emotional reaction or daily mood</li>
                            <li data-id="element-1431">• Every relationship outcome or interaction</li>
                            <li data-id="element-1432">• Temporary phases or situational responses</li>
                            <li data-id="element-1433">
                              • Learned skills, behaviors, or conscious choices
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CLOSURE PHASE 3: Integration Summary */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1434">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1435">
                    <span className="text-2xl" data-id="element-1436">🧭</span>
                    <div className="flex-1" data-id="element-1437">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1438">
                        Integration Summary
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1439">
                        This is a system that stabilizes through internal
                        structure and control. Under pressure, it tightens,
                        analyzes, and withdraws to create clarity before acting.
                        The pattern keeps repeating because it reliably reduces
                        short-term anxiety, even when it creates long-term
                        friction. Harmony occurs when external conditions allow
                        autonomy and depth; friction occurs when speed,
                        visibility, or ambiguity dominate. The pattern is not
                        the problem — the mismatch between pattern and
                        environment is.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-1440">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1441">
                    Save Diagnostic
                  </button>
                  <button onClick={() => setCurrentPhase('baseline-output')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-1442">
                    Revisit Baseline
                  </button>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}